/**
 * Storage Migration Utility
 * Automatically clears stale browser storage when app version changes
 * Prevents cached data from causing issues after breaking changes
 */

// Version tracking - increment when making breaking changes that affect storage
const CURRENT_STORAGE_VERSION = '2025.1.1'; // Incremented to test migration
const STORAGE_VERSION_KEY = 'so_lookup_storage_version';

/**
 * Storage items that should be cleared on breaking changes
 */
const STORAGE_ITEMS_TO_CLEAR = [
  // Authentication and session data that might have changed structure
  'admin_dev_session',
  'sales_session', 
  'site_password_session',
  'auth_session_data',
  
  // Cache and preloader data that might have stale format
  'backgroundPreloader_scheduledOfferings',
  'backgroundPreloader_sessions', 
  'backgroundPreloader_soSearch',
  'backgroundPreloader_lastUpdate',
  
  // API response caches that might have changed structure
  'airtable_cache',
  'typeform_cache',
  'api_response_cache',
  
  // Component state that might be incompatible
  'program_manager_state',
  'attendance_cache',
  'so_search_cache',
  
  // Any other app-specific cached data
  'so_lookup_cache',
  'session_data'
];

/**
 * Pattern-based cache keys to clear (for dynamic cache names)
 * Using app-specific prefixes to avoid clearing unrelated keys
 */
const CACHE_PATTERNS = [
  'so_lookup_airtable_',
  'so_lookup_typeform_',
  'so_lookup_cache_',
  'so_lookup_preloader_',
  'so_lookup_session_',
  'so_lookup_auth_',
  'so_lookup_api_',
  'backgroundPreloader_',  // Existing preloader pattern
  'pm_authenticated',      // Password protection key
  'admin_dev_session',     // Admin session key
  'sales_session'          // Sales session key
];

/**
 * Check if storage migration is needed and perform it
 * @returns {boolean} True if migration was performed, false if not needed
 */
export function checkAndMigrateStorage() {
  try {
    const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
    
    // If version matches, no migration needed
    if (storedVersion === CURRENT_STORAGE_VERSION) {
      if (import.meta.env.DEV) {
        console.log(`✅ Storage version up to date: ${CURRENT_STORAGE_VERSION}`);
      }
      return false;
    }
    
    if (import.meta.env.DEV) {
      console.log(`🔄 Storage migration needed. Stored: ${storedVersion || 'none'}, Current: ${CURRENT_STORAGE_VERSION}`);
    }
    
    // Clear specific storage items
    let itemsCleared = 0;
    STORAGE_ITEMS_TO_CLEAR.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        itemsCleared++;
      }
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        itemsCleared++;
      }
    });
    
    // Clear pattern-based cache keys from both localStorage and sessionStorage
    const localKeysToRemove = [];
    const sessionKeysToRemove = [];
    
    // Collect matching keys from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && CACHE_PATTERNS.some(pattern => key.startsWith(pattern))) {
        localKeysToRemove.push(key);
      }
    }
    
    // Collect matching keys from sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && CACHE_PATTERNS.some(pattern => key.startsWith(pattern))) {
        sessionKeysToRemove.push(key);
      }
    }
    
    // Remove from localStorage
    localKeysToRemove.forEach(key => {
      localStorage.removeItem(key);
      itemsCleared++;
    });
    
    // Remove from sessionStorage
    sessionKeysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
      itemsCleared++;
    });
    
    // Update to current version
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION);
    
    if (import.meta.env.DEV) {
      console.log(`✅ Storage migration completed - ${itemsCleared} items cleared`);
    }
    
    return true;
    
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('❌ Storage migration failed:', error);
    }
    
    // Do NOT set version on failure - this allows future retries
    // Return false to indicate migration was attempted but failed
    return false;
  }
}

/**
 * Show user-friendly notification about storage clearing
 * Returns a message object that can be used with the toast system
 */
export function showStorageMigrationNotification() {
  // Log to console for debugging in development only
  if (import.meta.env.DEV) {
    console.log('📦 Your browser data has been refreshed for the latest app updates');
  }
  
  // Return notification data for toast system
  return {
    title: "App Updated",
    description: "Your browser data has been refreshed for the latest updates. The app may feel snappier now!",
    type: "info"
  };
}

/**
 * Force clear all app-related storage (emergency use only)
 * @returns {boolean} Success status
 */
export function forceStorageClear() {
  try {
    // Get all keys first
    const localKeys = [];
    const sessionKeys = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) localKeys.push(key);
    }
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) sessionKeys.push(key);
    }
    
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Set current version
    localStorage.setItem(STORAGE_VERSION_KEY, CURRENT_STORAGE_VERSION);
    
    if (import.meta.env.DEV) {
      console.log(`🧹 Force cleared all storage - ${localKeys.length + sessionKeys.length} items removed`);
    }
    return true;
    
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Force storage clear failed:', error);
    }
    return false;
  }
}

/**
 * Get storage version info for debugging
 */
export function getStorageVersionInfo() {
  const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
  
  return {
    currentVersion: CURRENT_STORAGE_VERSION,
    storedVersion: storedVersion,
    needsMigration: storedVersion !== CURRENT_STORAGE_VERSION,
    storageSize: {
      localStorage: localStorage.length,
      sessionStorage: sessionStorage.length
    }
  };
}